import { useState, useCallback, useEffect, useRef } from 'react';
import { api } from '../api/client';

// useFolderPlan — the deal's Claude-generated folder structure.
//
// Claude reads the deal's documents and proposes the folders that fit them plus
// a home for each document; applying the plan creates those folders in
// SharePoint and moves the files. Two very different operations, so they're
// separate calls here too:
//
//   generate()  read-only. Safe to run on its own, and `auto` does exactly that
//               the first time a deal with documents has no plan.
//   apply(ids)  moves live files. Only ever called from an explicit user action,
//               with the exact set of documents the user ticked.
//
// Generation runs inline in the request and can take a while on a large deal,
// so `generating` is exposed for the button state rather than blocking a read.
// Both of these endpoints do their work inline in the request, so they need far
// more than the API client's 10s default. Bounded below the platform's ~230s
// HTTP limit, so a real hang still surfaces as a timeout rather than hanging the
// UI forever.
const PLAN_TIMEOUT_MS  = 180000;   // read the documents + one Claude call
const APPLY_TIMEOUT_MS = 210000;   // create folders + one Graph move per file

// While a run is `pending`, follow it by polling the read path. Bounded so a
// forgotten modal can't poll forever; the server heals a run that outlives its
// own window, so the poll always terminates in a real status.
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_TICKS   = 200;      // ~10 min, matching the server's stale window

export function useFolderPlan({ dealId, dealDetailId, enabled = true, auto = false } = {}) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(!!enabled);
  const [generating, setGenerating] = useState(false);
  const [applying, setApplying]   = useState(false);
  const [error, setError]         = useState(null);
  // A timeout may still be running server-side; a 500 is over. The UI has to
  // say different things about those, so the distinction is carried out.
  const [errorIsTimeout, setErrorIsTimeout] = useState(false);
  const reqRef  = useRef(0);
  // One auto-generate per deal per mount — a picker opened ten times must not
  // fire ten Claude calls.
  const autoRef = useRef(null);

  const scope = useCallback(() => ({
    ...(dealId ? { dealId } : {}),
    ...(dealDetailId ? { dealDetailId } : {}),
  }), [dealId, dealDetailId]);

  // `silent` is for the recovery read after a failed generate: it must not touch
  // `error`, or it would clear the very message it was called to preserve — and
  // the click would look like a no-op all over again.
  const load = useCallback(async ({ silent = false } = {}) => {
    if (!enabled || (!dealId && !dealDetailId)) { setLoading(false); setData(null); return null; }
    const seq = ++reqRef.current;
    setLoading(true);
    if (!silent) setError(null);
    try {
      const qs = new URLSearchParams(scope());
      const res = await api.get(`document-folders/plan?${qs.toString()}`);
      if (seq !== reqRef.current) return null;
      setData(res);
      return res;
    } catch (e) {
      if (seq === reqRef.current && !silent) { setError(e.message); setData(null); }
      return null;
    } finally {
      if (seq === reqRef.current) setLoading(false);
    }
  }, [enabled, dealId, dealDetailId, scope]);

  useEffect(() => { load(); }, [load]);   // eslint-disable-line react-hooks/exhaustive-deps

  const generate = useCallback(async ({ force = false } = {}) => {
    setGenerating(true); setError(null); setErrorIsTimeout(false);
    try {
      // Generation runs INLINE in the request: it reads the deal's documents and
      // calls Claude, which on a large deal takes far longer than the client's
      // 10s default. That default is why this used to look like nothing happened
      // — the request was aborted at 10s while the server carried on working,
      // and the abort error was never shown anywhere.
      const res = await api.post('document-folders/plan',
        { ...scope(), force }, { timeout: PLAN_TIMEOUT_MS });
      setData(res);
      return res;
    } catch (e) {
      // Re-read before deciding this is an error. The server runs on regardless
      // of us giving up, so the row is the truth: if the run is still `pending`
      // we report progress and let the poll above finish the job, rather than
      // showing a failure for something that is actively working. On a real
      // failure the read also picks up the reason the server recorded, instead
      // of a bare "Internal server error".
      const after = await load({ silent: true }).catch(() => null);
      if (after?.status !== 'pending') {
        setError(e.message);
        setErrorIsTimeout(!!e.isTimeout);
      }
      throw e;
    }
    finally { setGenerating(false); }
  }, [scope, load]);

  // ── Follow a run in flight ────────────────────────────────────────────────
  // The server marks the run `pending` before it starts work and checkpoints as
  // it goes, so progress comes from polling the read path rather than from the
  // generate response. Bounded: a run that outlives this is healed to an error
  // server-side on the next read, so the poll always ends somewhere.
  useEffect(() => {
    if (data?.status !== 'pending') return undefined;
    let cancelled = false;
    let ticks = 0;
    const id = setInterval(() => {
      if (cancelled || ++ticks > POLL_MAX_TICKS) { clearInterval(id); return; }
      // Silent: a transient blip mid-poll shouldn't replace live progress with
      // an error banner.
      load({ silent: true }).catch(() => {});
    }, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [data?.status, load]);

  // Generate once, unprompted, for a deal that has documents but no plan yet.
  // Deliberately never fires for a stale plan: regenerating over a plan someone
  // may be part-way through reviewing is a decision for them, not for a page load.
  useEffect(() => {
    if (!auto || !data || generating) return;
    const key = dealId || dealDetailId;
    if (!key || autoRef.current === key) return;
    if (data.available === false) return;
    if (data.status !== 'none' || !data.documentCount) return;
    autoRef.current = key;
    generate().catch(() => {});
  }, [auto, data, generating, dealId, dealDetailId, generate]);

  const apply = useCallback(async ({ documentIds, foldersOnly } = {}) => {
    setApplying(true); setError(null);
    try {
      // Applying creates folders and moves every selected file in SharePoint —
      // one Graph round-trip per document, so well past the 10s default too.
      const res = await api.post('document-folders/plan/apply',
        { ...scope(), documentIds, foldersOnly }, { timeout: APPLY_TIMEOUT_MS });
      await load();
      return res;
    } catch (e) { setError(e.message); throw e; }
    finally { setApplying(false); }
  }, [scope, load]);

  return { data, loading, generating, applying, error, errorIsTimeout, reload: load, generate, apply };
}
